<template>
  <div class="date-and-people">
    <p
      v-if="titleVisible"
      class="mb-3"
    >
      {{ managerFirstName }} needs these details to respond:
    </p>

    <FormGroup
      v-show="modal && $store.getters.isRtbFlowPossible && !$store.state.requestToBookPath || !searchedEventType"
      label="Event type"
      ref="eventType"
      :class="{'mb-4': modal}"
    >
      <SearchField
        v-model="form.eventType.value"
        :options="$store.state.eventTypes"
        :errors="form.eventType.errors"
        remove-value-on-type
        placeholder="What event are you planning?"
        data-test-id="enquiry-form__event-type"
        option-size="large"
        @focus="searchFieldFocus"
        @update:modelValue="updateField('eventType')"
        @click="track('event_tag_slug', 'click')"
        @change="track('event_tag_slug', 'change', $event)"
      />
    </FormGroup>

    <div v-if="$store.getters.isLayoutAndExtendedCateringFlow" class="flex">
      <PeopleField
        v-model="form.people.value"
        class="w-full mr-4"
        :class="{'mb-4': modal}"
        :errors="form.people.errors"
        :min-capacity="!$store.getters.isRtbFlowPossible && room?.capacity.min > 1 ? room.capacity.min : null"
        :max-capacity="room.capacity.max"
        @update:modelValue="updateField('people'); track('people', 'change', $event)"
        @click="track('people', 'click')"
      />

      <FormGroup class="w-full" label="Layout">
        <SelectField
          v-model="form.layout.value"
          :options="[{value: 'seating', label: 'Seating layout'}, {value: 'standing', label: 'Standing layout'}, { value: 'flexible', label: 'Any layout'}]"
          placeholder="Select layout"
          :errors="form.layout.errors"
          @update:modelValue="updateField('layout'); track('layout', 'change', $event)"
          @selectClick="track('layout', 'click')"
        />
      </FormGroup>
    </div>

    <div class="flex mb-4" ref="dateContainer">
      <FormGroup
        :label="eventTypeSpecificCopy.eventDate"
        class="w-full"
        :class="{'md:w-1/2 md:mr-2': !forcePeopleFieldSeparate && !$store.getters.isLayoutAndExtendedCateringFlow}"
        data-test-id="enquiry-form__date__label"
      >
        <template #postfix v-if="modal && $store.getters.isRtbFlowPossible && $store.state.requestToBookPath">
          <span v-if="!showFullDayRateInfo && $store.state.room.minHoursForBooking && $store.state.room.minHoursForBooking > 1" class="text-grey-600 font-normal ml-1">(min. {{ room.minHoursForBooking }} hours)</span>
        </template>

        <!-- be aware test-id="enquiry-form__date" is used for datepicker showing -->
        <DateField
          v-model="form.date.value"
          :ui-date-format="datepickerFormat"
          :week-start="$store.state.l10n.weekStart"
          :partially-booked-dates="room.availability.partiallyBookedDays"
          :fully-booked-dates="room.availability.fullyBookedDays"
          :closed-days-ids="room.availability.closedDaysOfWeek"
          data-test-id="enquiry-form__date"
          class="date-field"
          :is-in-modal="modal"
          placeholder="Select date"
          :notice-hours="$store.state.noticeHours"
          self-contain
          @update:modelValue="updateDate"
          @click="track('event_date', 'click')"
        />
        <FormCaption v-if="form.date.errors.length > 0" type="invalid" class="mt-1">
          <span class="flex items-center h-full" v-html="form.date.errors[0]" />
        </FormCaption>
      </FormGroup>

      <PeopleField
        v-if="isBigDesktop && !forcePeopleFieldSeparate && !$store.getters.isLayoutAndExtendedCateringFlow"
        v-model="form.people.value"
        class="w-1/2 ml-2"
        data-test-id="enquiry-form__people"
        :errors="form.people.errors"
        :min-capacity="!$store.getters.isRtbFlowPossible && room?.capacity.min > 1 ? room.capacity.min : null"
        :max-capacity="room.capacity.max"
        @update:modelValue="updateField('people');"
        @change="track('people', 'change', $event);"
        @click="track('people', 'click')"
      />
    </div>

    <TimeFields
      v-if="!isWedding"
      class="mb-4"
      :model-value="[form.timeFrom.value, form.timeTo.value]"
      :date="form.date.value"
      :room="$store.state.room"
      :l10n="$store.state.l10n"
      @update:modelValue="updateTime"
      @selectClick="(element) => track(element, 'click')"
    />

    <CheckboxField
      v-if="!isWedding && (!$store.state.roomSetupAllowsOP || $store.state.roomSetupAllowsOP && modal && !$store.state.requestToBookPath)"
      v-model="form.flexibleOnTime.value"
      class="mb-4"
      label="I'm flexible on dates and time"
      data-test-id="enquiry-form__flexible-on-time"
      @update:modelValue="updateFlexibleOnTime"
    />

    <FormCaption
      v-if="errorNoTimeSelected"
      type="invalid"
      class="-mt-2 mb-4 flex"
      :class="{'-mt-1': modal}"
    >
      <div
        v-if="$store.getters.isRtbFlowPossible"
        class="pt-0.5"
      >
        Please select event time
      </div>
      <div
        v-else
        class="pt-0.5"
        style="margin-top: -2px"
      >
        <div>
          Please select event time or select
        </div>
        <div class="italic">
          I’m flexible on dates and time
        </div>
      </div>
    </FormCaption>

    <FullDayRateInfo v-if="showFullDayRateInfo" class="pt-2 sm:pb-6" :price="minimumPricing.perDay.price.formatted" />

    <MinimumBookingTimeWarning
      v-else-if="showMinBookingWarning"
      :min-hours-for-booking="room.minHoursForBooking"
    />

    <div :class="{'mt-8': modal}">
      <PeopleField
        v-if="(!isBigDesktop || forcePeopleFieldSeparate) && !$store.getters.isLayoutAndExtendedCateringFlow"
        v-model="form.people.value"
        class="w-full"
        data-test-id="enquiry-form__people"
        :errors="form.people.errors"
        :min-capacity="!$store.getters.isRtbFlowPossible && room?.capacity?.min > 1 ? room.capacity.min : null"
        :max-capacity="room.capacity.max"
        @update:modelValue="updateField('people');"
        @change="track('people', 'change', $event);"
        @click="track('people', 'click')"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import CheckboxField from '@/components/Ui/Forms/CheckboxField.vue';
import DateField from '@/components/Ui/Forms/DateField.vue';
import FormCaption from '@/components/Ui/Forms/FormCaption.vue';
import FormGroup from '@/components/Ui/Forms/FormGroup.vue';
import SearchField from '@/components/Ui/Forms/SearchField.vue';
import SelectField from '@/components/Ui/Forms/SelectField.vue';
import MinimumBookingTimeWarning from '@/modules/public/enquiry-form/components/EnquiryForm/components/MinimumBookingTimeWarning.vue';
import FullDayRateInfo from '@/modules/public/enquiry-form/components/FullDayRateInfo.vue';
import { trackingMapFieldToLabel } from '@/modules/public/enquiry-form/store/actions/tracking.actions.js';
import formValidation, { FORM_VALIDATION } from '@/services/formValidation/index.js';
import { HOURS_MAP_24, isRoomAvailableInSelectedHours, isRoomClosed } from '@/services/hours-map';
import isTouchDevice from '@/utils/isTouchDevice';

import PeopleField from './fields/PeopleField.vue';
import TimeFields from './fields/TimeFields.vue';

export default {
  name: 'DateAndPeople',
  emits: ['track'],
  mixins: [formValidation],
  components: { SelectField, FullDayRateInfo, PeopleField, MinimumBookingTimeWarning, TimeFields, FormCaption, CheckboxField, DateField, SearchField, FormGroup },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    forcePeopleFieldSeparate: {
      type: Boolean,
      default: false,
    },
    titleVisible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const form = this.$store.state.form.dateAndPeople;

    return {
      form: {
        eventType: {
          value: form.eventType,
          errors: [],
        },
        date: {
          value: form.date,
          errors: [],
        },
        layout: {
          value: form.layout,
          errors: [],
        },
        people: {
          value: form.people,
          errors: [],
        },
        timeFrom: {
          value: form.timeFrom,
          errors: [],
        },
        timeTo: {
          value: form.timeTo,
          errors: [],
        },
        flexibleOnTime: {
          value: form.flexibleOnTime,
          errors: [],
        },
      },
      formValidation: {
        eventType: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter an event type',
        },
        date: {
          [FORM_VALIDATION.REQUIRED]: 'Please select event date',
        },
        people: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter number of people',
          [FORM_VALIDATION.MIN_NUMBER]: {
            message: 'Please enter number of people',
            value: 1,
          },
        },
      },
      errorNoTimeSelected: false,
      showMinBookingWarningWatcher: null,
    };
  },

  computed: {
    ...mapState({
      room: state => state.room,
      searchedEventType: state => state.user.searchedEventType,
      managerFirstName: state => state.manager.firstName,
      datepickerFormat: state => state.l10n.datepickerFormat,
      isBigDesktop: state => state.isBigDesktop,
      minimumPricing: state => state.minimumPricing,
    }),

    ...mapGetters([
      'eventTypeSpecificCopy',
      'isWedding',
    ]),

    lessThan7HoursSelected() {
      if (!this.form.timeFrom.value || !this.form.timeTo.value) {
        return false;
      }

      return this.form.timeTo.value - this.form.timeFrom.value < 14;
    },

    showFullDayRateInfo() {
      return this.$store.getters.isRtbFlowPossible
        && this.lessThan7HoursSelected
        && this.minimumPricing?.perDay
        && !this.minimumPricing?.perHour;
    },

    peopleOptions() {
      const people = parseInt(this.form.people.value, 10);
      const isBelowMinCapacity = people < this.room.capacity.min;
      const isOverCapacity = people > this.room.capacity.max;

      let className = '';
      let capacitySuffix = '';
      if (isBelowMinCapacity) {
        className = 'people-below-capacity';
        capacitySuffix = `(min ${this.room.capacity.min})`;
      } else if (isOverCapacity) {
        className = 'people-over-capacity';
        capacitySuffix = `(max ${this.room.capacity.max})`;
      }

      return {
        placeholder: `Max ${this.room.capacity.max}`,
        label: `${this.eventTypeSpecificCopy.people} ${capacitySuffix}`,
        className,
      };
    },

    showMinBookingWarning() {
      const selectedTime = (this.form.timeTo.value - this.form.timeFrom.value) / 2;
      if (Number.isNaN(selectedTime) || !this.form.timeTo.value || !this.form.timeFrom.value) {
        return false;
      }
      return this.room.minHoursForBooking > selectedTime;
    },
  },

  watch: {
    'form.eventType.value': function () {
      this.updateVuexStore();
    },
    'form.people.value': function () {
      this.updateVuexStore();
    },
    'form.layout.value': function () {
      this.updateVuexStore();
    },
    'form.flexibleOnTime.value': function () {
      this.updateVuexStore();
    },
    'form.date.value': function () {
      this.updateVuexStoreAndUpdatePricing();
    },
    'form.timeFrom.value': function () {
      this.updateVuexStoreAndUpdatePricing();
    },
    'form.timeTo.value': function () {
      this.updateVuexStoreAndUpdatePricing();

      if (!this.form.date.value && (this.form.timeFrom.value || this.form.timeTo.value)) {
        this.showDatepicker();
      }
    },
  },

  methods: {
    customFieldValidate(fieldName) {
      if (fieldName === 'layout' && this.$store.getters.isLayoutAndExtendedCateringFlow && !this.form.layout.value) {
        this.form.layout.errors = ['Please select layout'];
        return false;
      }

      if (fieldName !== 'flexibleOnTime' || this.isWedding) {
        return true;
      }
      const isValid = (this.form.timeFrom.value && this.form.timeTo.value &&
        this.form.timeTo?.value !== this.form.timeFrom?.value) || this.form.flexibleOnTime.value;
      if (!isValid) {
        this.errorNoTimeSelected = true;
      }
      return isValid;
    },

    submit() {
      const isValid = this.validateFields();
      if (isValid) {
        this.updateVuexStore();
      } else if (this.form.date.errors?.length) {
        this.showDatepicker();
      }
      return { isValid, data: this.form };
    },

    showDatepicker() { // showDatepicker
      this.$nextTick(() => { // wait for the errors to show in (specially on modal)
        if (!isTouchDevice()) { // @see TM-2608
          window.PIKADAY_INSTANCES['enquiry-form__date'].show();
        }
      });
    },

    updateVuexStore() {
      this.$store.commit('setDateAndPeopleForm', this.form);
    },

    updateVuexStoreAndUpdatePricing() {
      this.$store.commit('setDateAndPeopleForm', this.form);
      this.$store.dispatch('getRoomPricing');
      this.$store.dispatch('getMinimumPricing');
    },

    updateDate() {
      const date = this.form.date.value;
      const timeFrom = this.form.timeFrom.value;
      const timeTo = this.form.timeTo.value;

      if (date && timeFrom && timeTo && !isRoomAvailableInSelectedHours(date, timeFrom, timeTo, this.room.availability)) {
        this.form.timeFrom.value = null;
        this.form.timeTo.value = null;
      }
      this.updateField('date');

      const eventDate = window.dayjs(date, this.datepickerFormat).format('YYYY-MM-DD');
      this.track('event_date', 'change', eventDate);
    },

    updateTime([timeFrom, timeTo], element) {
      this.form.timeFrom.value = timeFrom;
      this.form.timeTo.value = timeTo;
      this.errorNoTimeSelected = false;

      this.$store.dispatch('trackEnquiryFormValueChange', {
        label: 'time',
        values: this.values,
        errors: this.errors,
      });

      if (element) {
        this.track(element, 'change', element === 'time_from' ? HOURS_MAP_24[timeFrom] : HOURS_MAP_24[timeTo]);
      }
    },

    updateFlexibleOnTime(value) {
      this.errorNoTimeSelected = false;

      this.$store.dispatch('trackEnquiryFormValueChange', {
        label: 'flexible',
        values: this.values,
        errors: this.errors,
      });

      this.track('flexible_date', 'change', value);
    },

    updateField(fieldName) {
      this.form[fieldName].errors = [];

      this.$store.dispatch('trackEnquiryFormValueChange', {
        label: trackingMapFieldToLabel(fieldName),
        values: this.values,
        errors: this.errors,
      });
    },

    searchFieldFocus() {
      if (!this.isBigDesktop) {
        this.$refs.eventType.$el.scrollIntoView({
          behavior: 'smooth',
        });
      }
    },
    onMinBookingWarningVisible() {
      this.track('event_duration_warning', 'visible');
    },
    addShowMinBookingWarningWatcher() {
      this.showMinBookingWarningWatcher = this.$watch('showMinBookingWarning', (newValue) => {
        if (!newValue) {
          return;
        }
        this.onMinBookingWarningVisible();
      });

      if (this.showMinBookingWarning) {
        this.onMinBookingWarningVisible();
      }
    },
    track(element, interaction, value = null) {
      this.$emit('track', {
        element,
        interaction,
        value,
      });
    },
  },
  beforeUnmount() {
    if (this.showMinBookingWarningWatcher) {
      this.showMinBookingWarningWatcher(); // unwatch
    }
  },
  mounted() {
    const date = this.form.date.value;
    if (date && isRoomClosed(date, this.room.availability)) {
      this.form.date.value = null;
      this.form.timeFrom.value = null;
      this.form.timeTo.value = null;
    }

    this.addShowMinBookingWarningWatcher();
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

.date-field {
  :deep(input) {
    cursor: pointer;
  }

  :deep(.ds-text-field__arrow ~ .ds-text-field__input) {
    padding-right: 24px;
  }
}

.people-capacity-warning :deep(.ds-form-label) {
  color: ds-variables.$yellow;
}
.people-capacity-warning :deep(input) {
  border-color: ds-variables.$yellow;
}

.date-and-people :deep(.ds-text-field__arrow),
.date-and-people :deep(.ds-select-field__arrow) {
  right: 12px;
}
</style>

<style lang="scss">
  .fixed-important {
    position: fixed !important;
  }
</style>
